import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { MapContainer, TileLayer, Polyline, useMap, CircleMarker, Marker, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import { Play, Square, Map as MapIcon, Info, MapPin, RefreshCw, AlertCircle } from 'lucide-react';
import { calculateDistance, getMETForSpeed, calculateCalories } from '../utils/fitness';

// Fix for default marker icons in Leaflet + React
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Helper component to center the map on the current position
const RecenterMap = ({ position }) => {
  const map = useMap();
  useEffect(() => {
    // Small delay to ensure container has settled
    const timer = setTimeout(() => {
      map.invalidateSize();
    }, 500);
    
    if (position) {
      map.setView(position, 16);
    }
    return () => clearTimeout(timer);
  }, [position, map]);
  return null;
};

const LiveTracker = ({ userWeight, onSave }) => {
  const { t } = useTranslation();
  // Use 60kg as a general fallback if weight is not provided
  const effectiveWeight = userWeight > 0 ? userWeight : 60;
  
  const [isTracking, setIsTracking] = useState(false);
  const [path, setPath] = useState([]); // [{lat, lng, timestamp}]
  const [distance, setDistance] = useState(0);
  const [duration, setDuration] = useState(0); // in seconds
  const [calories, setCalories] = useState(0);
  const [showMap, setShowMap] = useState(true);
  
  const [isLocating, setIsLocating] = useState(false);
  const [locationError, setLocationError] = useState(null);
  
  const timerRef = useRef(null);
  const watchIdRef = useRef(null);
  const wakeLockRef = useRef(null);

  // Screen Wake Lock logic
  const requestWakeLock = async () => {
    if ('wakeLock' in navigator) {
      try {
        wakeLockRef.current = await navigator.wakeLock.request('screen');
        console.log('Wake Lock is active');
      } catch (err) {
        console.error(`${err.name}, ${err.message}`);
      }
    }
  };

  const releaseWakeLock = async () => {
    if (wakeLockRef.current) {
      await wakeLockRef.current.release();
      wakeLockRef.current = null;
      console.log('Wake Lock released');
    }
  };

  const handleDetectLocation = () => {
    if (!('geolocation' in navigator)) {
      setLocationError('Geolocation not supported');
      return;
    }

    setIsLocating(true);
    setLocationError(null);

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        setPath([{ lat: latitude, lng: longitude, timestamp: Date.now() }]);
        setIsLocating(false);
      },
      (error) => {
        console.error('GPS Error:', error);
        let errorMsg = t('tracking.location_error');
        if (error.code === 1) {
          errorMsg = t('tracking.location_permission_denied');
        } else if (error.code === 2) {
          errorMsg = t('tracking.location_unavailable');
        } else if (error.code === 3) {
          errorMsg = t('tracking.location_timeout');
        }
        setLocationError(errorMsg);
        setIsLocating(false);
      },
      { 
        enableHighAccuracy: false, // Start with lower accuracy for speed/reliability in Safari
        timeout: 15000,           // Longer timeout for the initial fix
        maximumAge: 10000         // Allow cached position up to 10s old
      }
    );
  };

  useEffect(() => {
    if (isTracking) {
      requestWakeLock();
      
      // Re-request wake lock if visibility changes (e.g. user briefly tabs out)
      const handleVisibilityChange = () => {
        if (document.visibilityState === 'visible' && isTracking) {
          requestWakeLock();
        }
      };
      document.addEventListener('visibilitychange', handleVisibilityChange);

      // Start Timer
      timerRef.current = setInterval(() => {
        setDuration(prev => prev + 1);
      }, 1000);

      // Start GPS Tracking
      if ('geolocation' in navigator) {
        watchIdRef.current = navigator.geolocation.watchPosition(
          (position) => {
            const { latitude, longitude } = position.coords;
            const newPoint = { lat: latitude, lng: longitude, timestamp: Date.now() };
            
            setPath(prevPath => {
              if (prevPath.length > 0) {
                const lastPoint = prevPath[prevPath.length - 1];
                const d = calculateDistance(lastPoint.lat, lastPoint.lng, latitude, longitude);
                setDistance(prevD => prevD + d);
              }
              return [...prevPath, newPoint];
            });
          },
          (error) => console.error('GPS Error:', error),
          { enableHighAccuracy: true, timeout: 10000, maximumAge: 2000 }
        );
      }
      return () => {
        document.removeEventListener('visibilitychange', handleVisibilityChange);
        releaseWakeLock();
      }
    } else {
      releaseWakeLock();
      if (timerRef.current) clearInterval(timerRef.current);
      if (watchIdRef.current) navigator.geolocation.clearWatch(watchIdRef.current);
    }

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (watchIdRef.current) navigator.geolocation.clearWatch(watchIdRef.current);
    };
  }, [isTracking]);

  // Recalculate calories periodically
  useEffect(() => {
    if (duration > 0) {
      const speed = distance > 0 ? (distance / (duration / 3600)) : 0;
      const met = getMETForSpeed(speed);
      const cals = calculateCalories(met, effectiveWeight, duration / 3600);
      setCalories(Math.round(cals));
    }
  }, [duration, distance, effectiveWeight]);

  const handleStart = () => {
    if (path.length === 0) {
      handleDetectLocation();
      return;
    }
    setIsTracking(true);
  };
  const handleStop = () => {
    setIsTracking(false);
    if (onSave) {
      onSave({
        path,
        distance: distance.toFixed(2),
        duration,
        calories,
        timestamp: new Date().toISOString()
      });
    }
  };

  const currentPos = path.length > 0 ? [path[path.length - 1].lat, path[path.length - 1].lng] : null;

  return (
    <div className="flex flex-col h-full bg-slate-950 text-white rounded-2xl overflow-hidden shadow-2xl border border-slate-800">
      {/* Header / Stats Overlay */}
      <div className="p-6 bg-slate-900/80 backdrop-blur-md border-b border-slate-800 grid grid-cols-2 gap-4">
        <div>
          <p className="text-slate-400 text-xs uppercase tracking-wider font-semibold">{t('distance_label')}</p>
          <p className="text-3xl font-bold font-mono">{distance.toFixed(2)} <span className="text-sm font-normal">km</span></p>
        </div>
        <div>
          <p className="text-slate-400 text-xs uppercase tracking-wider font-semibold">{t('time_label')}</p>
          <p className="text-3xl font-bold font-mono">
            {Math.floor(duration / 60)}:{String(duration % 60).padStart(2, '0')}
          </p>
        </div>
        <div>
          <p className="text-slate-400 text-xs uppercase tracking-wider font-semibold">{t('tracking.avg_speed')}</p>
          <p className="text-xl font-bold font-mono">
            {duration > 0 ? (distance / (duration / 3600)).toFixed(1) : 0} <span className="text-sm font-normal">km/h</span>
          </p>
        </div>
        <div>
          <p className="text-slate-400 text-xs uppercase tracking-wider font-semibold">{t('calories')}</p>
          <p className="text-xl font-bold font-mono">
            {calories} <span className="text-sm font-normal">kcal</span>
            {!userWeight && <span className="text-[10px] text-amber-500 block font-sans font-normal leading-tight mt-1">{t('tracking.default_weight_notice')}</span>}
          </p>
        </div>
      </div>

      {/* Map Area */}
      <div className="flex-1 relative min-h-[300px]">
        {showMap ? (
          <MapContainer 
            center={currentPos || [35.6895, 139.6917]} 
            zoom={16} 
            style={{ height: '500px', width: '100%', minHeight: '400px' }}
            zoomControl={false}
          >
            <TileLayer
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            />
            {path.length > 0 && (
              <CircleMarker 
                center={[path[0].lat, path[0].lng]} 
                radius={8}
                pathOptions={{ color: 'white', fillColor: '#10b981', fillOpacity: 1, weight: 2 }} 
              />
            )}
            {path.length > 1 && (
              <Polyline positions={path.map(p => [p.lat, p.lng])} color="#f43f5e" weight={5} />
            )}
            {currentPos && (
              <>
                <CircleMarker 
                  center={currentPos} 
                  radius={8}
                  pathOptions={{ color: 'white', fillColor: '#3b82f6', fillOpacity: 1, weight: 2 }} 
                />
                <RecenterMap position={currentPos} />
              </>
            )}
          </MapContainer>
        ) : (
          <div className="flex items-center justify-center h-full bg-slate-900">
            <p className="text-slate-500 italic">{t('tracking.map_hidden')}</p>
          </div>
        )}
        
        {/* Map Toggle Button */}
        <button 
          type="button"
          onClick={() => setShowMap(!showMap)}
          className="absolute bottom-4 right-4 z-[1000] p-3 bg-slate-800/90 rounded-full shadow-lg border border-slate-700 hover:bg-slate-700 transition-colors"
        >
          {showMap ? <Info size={20} /> : <MapIcon size={20} />}
        </button>
      </div>

      {/* Controls */}
      <div className="p-8 bg-slate-900 border-t border-slate-800 flex flex-col items-center gap-4">
        {!isTracking ? (
          <>
            {path.length === 0 ? (
              <div className="flex flex-col items-center gap-4 w-full">
                {locationError && (
                  <div className="flex items-center gap-2 text-rose-400 bg-rose-400/10 px-4 py-2 rounded-lg text-sm border border-rose-400/20 mb-2">
                    <AlertCircle size={16} />
                    <span>{locationError}</span>
                  </div>
                )}
                <button 
                  type="button"
                  onClick={handleDetectLocation}
                  disabled={isLocating}
                  className="flex items-center justify-center gap-3 bg-blue-600 hover:bg-blue-500 disabled:bg-blue-800 text-white px-10 py-4 rounded-full font-bold text-lg shadow-lg shadow-blue-900/20 transition-all active:scale-95 whitespace-nowrap min-w-[240px]"
                >
                  {isLocating ? (
                    <>
                      <RefreshCw className="animate-spin" size={24} />
                      {t('tracking.locating')}
                    </>
                  ) : (
                    <>
                      <MapPin size={24} />
                      {t('tracking.detect_location')}
                    </>
                  )}
                </button>
              </div>
            ) : (
              <>
                <button 
                  type="button"
                  onClick={handleStart}
                  className="flex items-center justify-center gap-3 bg-rose-600 hover:bg-rose-500 text-white px-10 py-4 rounded-full font-bold text-lg shadow-lg shadow-rose-900/20 transition-all active:scale-95 whitespace-nowrap min-w-[240px]"
                >
                  <Play fill="white" size={24} /> {t('tracking.start_run')}
                </button>
                <div className="flex flex-col items-center gap-2">
                  <button 
                    type="button"
                    onClick={handleDetectLocation}
                    className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors text-sm"
                  >
                    <RefreshCw size={14} className={isLocating ? 'animate-spin' : ''} />
                    {t('tracking.detect_location')}
                  </button>
                </div>
              </>
            )}
            <p className="text-amber-400 text-[13px] font-bold text-center leading-relaxed max-w-[300px] mt-2">
              {t('tracking.sleep_warning')}
            </p>
          </>
        ) : (
          <button 
            type="button"
            onClick={handleStop}
            className="flex items-center justify-center gap-3 bg-slate-100 hover:bg-white text-slate-900 px-10 py-4 rounded-full font-bold text-lg shadow-lg transition-all active:scale-95 whitespace-nowrap min-w-[240px]"
          >
            <Square fill="currentColor" size={24} /> {t('tracking.stop_save')}
          </button>
        )}
      </div>
    </div>
  );
};

export default LiveTracker;
